import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Plus, Scale, FileText, Trash2, Edit2, Save, X, Download, Upload, Film, Link2, ArrowLeft, User, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  "Traffico di stupefacenti", "Rapina", "Furto aggravato", "Omicidio", "Aggressione",
  "Guida in stato di ebbrezza", "Detenzione illegale di armi", "Riciclaggio",
  "Falsificazione documenti", "Sequestro di persona", "Corruzione", "Altro",
];

const MANDATE_TYPES = [
  { value: "arresto", label: "Mandato d'Arresto" },
  { value: "ricerca_cattura", label: "Mandato di Ricerca e Cattura" },
  { value: "perquisizione", label: "Mandato di Perquisizione" },
  { value: "altro", label: "Altro (specificare)" },
];

const SUBJECT_ROLES = ["sospettato", "indagato", "testimone", "vittima"];

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function MandatiPanel() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | mandate object

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mandates").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return (
      <MandateDetail
        mandate={selected}
        onBack={() => setSelected(null)}
        onEdit={() => { setEditing(selected); setSelected(null); }}
        onDeleted={() => { setSelected(null); load(); }}
        onClipUploaded={(updated) => setSelected(updated)}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="mandati-panel">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-2">
            <Scale className="h-4 w-4" /> Ufficio Rapporti · Procura
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#F5E6B8] tracking-tighter">
            Richieste di Mandato
          </h2>
          <p className="text-sm text-[#D4C480] max-w-2xl mt-1">
            Compila una richiesta ufficiale di mandato al Procuratore Distrettuale.
            Al momento dell&apos;invio il bot apre automaticamente un forum in Discord e
            allega il PDF generato taggando la Procura.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} data-testid="new-mandate-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuova richiesta
        </Button>
      </div>

      {loading ? (
        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-sheriff-gold" /></div>
      ) : !items || items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sheriff-gold/25 p-10 text-center text-sheriff-gold/60">
          Nessuna richiesta di mandato presente. Clicca &quot;Nuova richiesta&quot; per crearne una.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`mandate-row-${m.case_number}`}
              className="w-full text-left rounded-xl border border-sheriff-gold/20 hover:border-sheriff-gold/60 bg-gradient-to-br from-[#143830] to-[#0F2620] px-4 py-3 flex items-center gap-4 group transition-all hover:-translate-y-0.5"
            >
              <div className="h-11 w-11 rounded-full border border-sheriff-gold/40 bg-sheriff-gold/10 text-sheriff-gold flex items-center justify-center dossier-mono text-[10px] font-bold shrink-0">
                {m.case_number.replace("M-", "").replace(String(new Date().getFullYear()) + "-", "")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[#F5E6B8] font-medium truncate group-hover:text-sheriff-gold">
                  {m.title}
                </div>
                <div className="dossier-mono text-[10px] uppercase tracking-wider text-sheriff-gold/60 mt-0.5 truncate">
                  {m.case_number} · {m.crime_category} · {m.redacted_by}
                </div>
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/50">
                  {m.created_at?.slice(0, 10)}
                </div>
                {m.clip_filename && <div className="text-[10px] text-emerald-400 mt-0.5">🎞️ Clip allegata</div>}
              </div>
              <ChevronRight className="h-5 w-5 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          ))}
        </div>
      )}

      {editing && (
        <MandateFormDialog
          mandate={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function MandateDetail({ mandate, onBack, onEdit, onDeleted, onClipUploaded }) {
  const { user } = useAuth();
  const isAuthor = user && (mandate.redacted_by_user_id === user.id);
  const [uploading, setUploading] = useState(false);

  const del = async () => {
    if (!confirm("Eliminare definitivamente questa richiesta? PDF e clip saranno rimossi.")) return;
    try {
      await api.delete(`/mandates/${mandate.id}`);
      toast.success("Richiesta eliminata");
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione fallita"); }
  };

  const uploadClip = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/mandates/${mandate.id}/clip`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Clip caricata");
      onClipUploaded(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-5" data-testid="mandate-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={onBack} variant="outline" size="sm" data-testid="back-to-mandates">
          <ArrowLeft className="h-4 w-4 mr-1" /> Torna
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" asChild data-testid="download-pdf">
          <a href={`${API_BASE}/mandates/${mandate.id}/pdf`} target="_blank" rel="noreferrer">
            <Download className="h-4 w-4 mr-1" /> Scarica PDF
          </a>
        </Button>
        {isAuthor && (
          <>
            <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-mandate">
              <Edit2 className="h-4 w-4 mr-1" /> Modifica
            </Button>
            <Button variant="outline" size="sm" onClick={del} className="text-rose-300 hover:text-rose-200 border-rose-500/40" data-testid="delete-mandate">
              <Trash2 className="h-4 w-4 mr-1" /> Elimina
            </Button>
          </>
        )}
      </div>

      <div className="rounded-2xl border border-sheriff-gold/30 bg-gradient-to-br from-[#0F2620] to-[#143830] p-6 space-y-4">
        <div>
          <div className="dossier-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/60">
            Pratica {mandate.case_number}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#F5E6B8] tracking-tight mt-1">
            {mandate.title}
          </h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#D4C480]">
            <span><User className="h-3 w-3 inline mr-1 text-sheriff-gold/60" />{mandate.redacted_by_role} {mandate.redacted_by}{mandate.redacted_by_badge && ` · ${mandate.redacted_by_badge}`}</span>
            <span>· {mandate.incident_datetime}</span>
            <span>· <span className="text-rose-300">{mandate.crime_category}</span></span>
          </div>
        </div>

        {mandate.subjects?.length > 0 && (
          <Section label="Soggetti coinvolti">
            <ul className="space-y-1">
              {mandate.subjects.map((s, i) => (
                <li key={`${s.name}-${i}`} className="text-sm">
                  <span className="text-[#F5E6B8]">{s.name}</span>
                  {s.dob && <span className="text-sheriff-gold/60"> · nato/a il {s.dob}</span>}
                  <span className="text-sheriff-gold/60"> · {s.role}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        <Section label="Descrizione dei fatti"><Prose text={mandate.description} /></Section>
        <Section label="Motivazione giuridica"><Prose text={mandate.justification} /></Section>
        <Section label="Tipo di mandato richiesto"><Prose text={mandate.requested_actions} /></Section>
        {mandate.evidence_text && (
          <Section label="Prove raccolte"><Prose text={mandate.evidence_text} /></Section>
        )}

        <Section label="Allegati multimediali">
          <div className="space-y-2">
            {mandate.clip_url && (
              <a href={mandate.clip_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sky-300 hover:text-sky-200 text-sm">
                <Link2 className="h-4 w-4" /> Link clip esterno
              </a>
            )}
            {mandate.clip_filename ? (
              <a href={`${API_BASE}/mandates/${mandate.id}/clip`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 text-sm">
                <Film className="h-4 w-4" /> {mandate.clip_filename}
              </a>
            ) : (
              !mandate.clip_url && (
                <div className="text-sheriff-gold/50 text-sm">Nessun allegato multimediale</div>
              )
            )}
            {isAuthor && (
              <div className="pt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-sheriff-gold/30 hover:border-sheriff-gold/60 text-sheriff-gold text-xs uppercase tracking-wider transition-all">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {mandate.clip_filename ? "Sostituisci clip" : "Carica clip video"}
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-matroska,image/gif"
                    className="hidden"
                    onChange={(e) => uploadClip(e.target.files?.[0])}
                    data-testid="upload-clip-input"
                  />
                </label>
                <div className="text-[10px] text-sheriff-gold/50 mt-1">
                  Formati: mp4, mov, webm, mkv, gif. Max 100 MB. Solo file &lt;25MB allegati automaticamente al forum Discord.
                </div>
              </div>
            )}
          </div>
        </Section>

        {mandate.discord_thread_id && (
          <div className="pt-2 border-t border-sheriff-gold/15 text-xs text-sheriff-gold/60 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Forum Discord aperto (thread {mandate.discord_thread_id})
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="uppercase-label text-xs text-sheriff-gold/70 mb-1">{label}</div>
      {children}
    </div>
  );
}
function Prose({ text }) {
  return <p className="text-sm text-[#D4C480] leading-relaxed whitespace-pre-wrap">{text}</p>;
}

function MandateFormDialog({ mandate, onClose, onSaved }) {
  const isNew = !mandate;
  // Deriva mandate_type dall'oggetto salvato: se il testo requested_actions corrisponde ad
  // uno dei tipi noti, seleziona quello; altrimenti tratta come "altro"
  const initialType = (() => {
    if (!mandate) return "arresto";
    const known = MANDATE_TYPES.slice(0, 3).find((t) => t.label === mandate.requested_actions);
    return known ? known.value : "altro";
  })();
  const initialCustom = (() => {
    if (!mandate) return "";
    const known = MANDATE_TYPES.slice(0, 3).find((t) => t.label === mandate.requested_actions);
    return known ? "" : (mandate.requested_actions || "");
  })();
  const [form, setForm] = useState(() => ({
    title: mandate?.title || "",
    incident_datetime: mandate?.incident_datetime || "",
    crime_category: mandate?.crime_category || "",
    description: mandate?.description || "",
    justification: mandate?.justification || "",
    evidence_text: mandate?.evidence_text || "",
    clip_url: mandate?.clip_url || "",
  }));
  const [mandateType, setMandateType] = useState(initialType);
  const [mandateTypeOther, setMandateTypeOther] = useState(initialCustom);
  const [subjects, setSubjects] = useState(
    () => (mandate?.subjects?.length
      ? mandate.subjects.map((s) => ({ id: crypto.randomUUID(), dob: "", ...s }))
      : [{ id: crypto.randomUUID(), name: "", dob: "", role: "sospettato" }])
  );
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));
  const setSubj = (idx, field, val) => setSubjects((s) => s.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const addSubj = () => setSubjects((s) => [...s, { id: crypto.randomUUID(), name: "", dob: "", role: "sospettato" }]);
  const rmSubj = (idx) => setSubjects((s) => s.filter((_, i) => i !== idx));

  // Costruisce il testo da inviare al backend per "provvedimenti richiesti"
  const buildRequestedActions = () => {
    if (mandateType === "altro") return mandateTypeOther.trim();
    const chosen = MANDATE_TYPES.find((t) => t.value === mandateType);
    return chosen?.label || "";
  };

  const save = async () => {
    if (form.title.trim().length < 3) { toast.error("Titolo troppo corto"); return; }
    if (form.description.trim().length < 20) { toast.error("Descrizione: minimo 20 caratteri"); return; }
    if (form.justification.trim().length < 20) { toast.error("Motivazione: minimo 20 caratteri"); return; }
    const requested = buildRequestedActions();
    if (requested.length < 5) { toast.error("Specifica il tipo di mandato richiesto"); return; }
    const clean_subjects = subjects.filter((s) => s.name.trim()).map(({ id: _id, ...rest }) => rest);
    setSaving(true);
    try {
      const payload = { ...form, subjects: clean_subjects, requested_actions: requested, clip_url: form.clip_url.trim() || null };
      if (isNew) {
        const { data } = await api.post("/mandates", payload);
        toast.success(data.discord_ok
          ? `Richiesta inviata · Forum aperto in Discord (${data.case_number})`
          : `Richiesta creata (${data.case_number}). Discord post fallito.`);
      } else {
        await api.patch(`/mandates/${mandate.id}`, payload);
        toast.success("Richiesta aggiornata");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0F2620] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8]"
      >
        <DialogHeader>
          <DialogTitle className="text-[#F5E6B8] font-heading">{isNew ? "Nuova richiesta di mandato" : `Modifica ${mandate.case_number}`}</DialogTitle>
          <DialogDescription className="text-[#D4C480]">
            {isNew
              ? "Compila la richiesta. Al salvataggio il bot apre un thread su Discord e tagga la Procura."
              : "Le modifiche rigenerano il PDF ma non ripubblicano il thread Discord."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <FormField label="Titolo *">
            <Input value={form.title} onChange={set("title")} placeholder="Es. Perquisizione domicilio via Vinewood 12"
              maxLength={140} data-testid="form-title" className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Data / ora evento *">
              <Input value={form.incident_datetime} onChange={set("incident_datetime")}
                placeholder="2026-07-14 22:30" data-testid="form-datetime"
                className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
            </FormField>
            <FormField label="Categoria reato *">
              <Select value={form.crime_category} onValueChange={set("crime_category")}>
                <SelectTrigger data-testid="form-category" className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <FormField label="Tipo di mandato *">
            <Select value={mandateType} onValueChange={setMandateType}>
              <SelectTrigger data-testid="form-mandate-type" className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MANDATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {mandateType === "altro" && (
              <Textarea
                value={mandateTypeOther}
                onChange={(e) => setMandateTypeOther(e.target.value)}
                rows={2}
                placeholder="Specifica il tipo di mandato richiesto..."
                data-testid="form-mandate-type-other"
                className="mt-2 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
              />
            )}
          </FormField>

          <FormField label="Soggetti coinvolti">
            <div className="space-y-2">
              {subjects.map((s, i) => (
                <div key={s.id} className="flex gap-2 items-center flex-wrap sm:flex-nowrap" data-testid={`subject-row-${i}`}>
                  <Input value={s.name} onChange={(e) => setSubj(i, "name", e.target.value)}
                    placeholder="Nome e cognome"
                    className="flex-1 min-w-[140px] bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
                    data-testid={`subject-name-${i}`} />
                  <Input value={s.dob || ""} onChange={(e) => setSubj(i, "dob", e.target.value)}
                    placeholder="Data di nascita"
                    className="w-full sm:w-40 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
                    data-testid={`subject-dob-${i}`} />
                  <Select value={s.role} onValueChange={(v) => setSubj(i, "role", v)}>
                    <SelectTrigger className="w-36 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]" data-testid={`subject-role-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SUBJECT_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {subjects.length > 1 && (
                    <Button size="icon" variant="outline" onClick={() => rmSubj(i)} type="button" data-testid={`rm-subject-${i}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addSubj} type="button" data-testid="add-subject-btn">
                <Plus className="h-4 w-4 mr-1" /> Aggiungi soggetto
              </Button>
            </div>
          </FormField>

          <FormField label="Descrizione dei fatti * (min 20 caratteri)">
            <Textarea value={form.description} onChange={set("description")} rows={4}
              placeholder="Descrivi l'accaduto in modo cronologico e dettagliato..." data-testid="form-description"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
          </FormField>
          <FormField label="Motivazione giuridica * (min 20 caratteri)">
            <Textarea value={form.justification} onChange={set("justification")} rows={3}
              placeholder="Spiega perché è necessario un mandato: fondati sospetti, urgenza, indizi..." data-testid="form-justification"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
          </FormField>
          <FormField label="Prove e prove raccolte (facoltativo)">
            <Textarea value={form.evidence_text} onChange={set("evidence_text")} rows={2}
              placeholder="Registrazioni bodycam, testimonianze, oggetti sequestrati..." data-testid="form-evidence"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
          </FormField>
          <FormField label="Link clip video esterno (Medal / YouTube / Streamable...)">
            <Input value={form.clip_url} onChange={set("clip_url")} placeholder="https://..." data-testid="form-clip-url"
              className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40" />
          </FormField>
          <div className="text-xs text-sheriff-gold/60">
            Dopo il salvataggio potrai anche caricare un file video allegato direttamente dalla pagina di dettaglio.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" />Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="form-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isNew ? "Invia alla Procura" : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="uppercase-label text-xs text-sheriff-gold mb-1 block">{label}</label>
      {children}
    </div>
  );
}
